<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("account.menu.reports") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <!-- Filters -->
      <card class="mb-4">
        <div class="row">
          <div class="col-md-3">
            <label class="form-control-label">{{ $t('account.date_range') }}</label>
            <el-date-picker
              v-model="filterDateRange"
              type="daterange"
              range-separator="-"
              :start-placeholder="$t('account.date_from')"
              :end-placeholder="$t('account.date_to')"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              class="w-100"
            >
            </el-date-picker>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t('account.type') }}</label>
            <el-select v-model="filterType" class="w-100" clearable>
              <el-option :label="$t('account.all')" :value="0"></el-option>
              <el-option :label="$t('account.income')" :value="1"></el-option>
              <el-option :label="$t('account.expense')" :value="2"></el-option>
            </el-select>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <base-button type="primary" @click="loadAllReports">
              <i class="fa fa-search"></i> {{ $t('search') }}
            </base-button>
          </div>
        </div>
      </card>

      <!-- Summary Cards -->
      <div class="row" v-if="reportSummary">
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('account.total_income')"
            type="gradient-green"
            :sub-title="formatNumber(reportSummary.total_income)"
            icon="ni ni-money-coins"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ reportSummary.income_count }} {{ $t('account.transactions') }}</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('account.total_expense')"
            type="gradient-red"
            :sub-title="formatNumber(reportSummary.total_expense)"
            icon="ni ni-cart"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ reportSummary.expense_count }} {{ $t('account.transactions') }}</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('account.net_amount')"
            :type="reportSummary.net_amount >= 0 ? 'gradient-info' : 'gradient-orange'"
            :sub-title="formatNumber(reportSummary.net_amount)"
            icon="ni ni-chart-bar-32"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ $t('account.balance') }}</span>
            </template>
          </stats-card>
        </div>
      </div>

      <!-- Date Chart -->
      <card class="mb-4">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col">
              <h5 class="h3 mb-0">{{ $t('account.chart_by_date') }}</h5>
            </div>
          </div>
        </template>
        <div class="chart-area" style="height: 350px;">
          <bar-chart
            v-if="dateChartData.labels.length > 0"
            :chart-data="dateChartData"
            :extra-options="chartOptions"
            :height="350"
          />
          <div v-else class="text-center text-muted py-5">
            {{ $t('account.no_data') }}
          </div>
        </div>
      </card>

      <!-- Reports Tabs -->
      <card>
        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <!-- By Item Tab -->
          <el-tab-pane :label="$t('account.report_by_item')" name="byItem">
            <el-table
              :data="reportByItem"
              row-key="account_item_id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('account.category')" min-width="200">
                <div slot-scope="{ row }">
                  {{ row.account_item_path || row.account_item_name }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.type')" min-width="100">
                <div slot-scope="{ row }">
                  <badge :type="row.type === 1 ? 'success' : 'danger'">
                    {{ row.type === 1 ? $t('account.income') : $t('account.expense') }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.total_amount')" min-width="150">
                <div slot-scope="{ row }" :class="row.type === 1 ? 'text-success' : 'text-danger'">
                  {{ formatNumber(row.total_amount) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.count')" min-width="100">
                <div slot-scope="{ row }">
                  {{ row.count }}
                </div>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- By User Tab -->
          <el-tab-pane :label="$t('account.report_by_user')" name="byUser">
            <el-table
              :data="reportByUser"
              row-key="user_id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('account.user_name')" min-width="200">
                <div slot-scope="{ row }">
                  {{ row.user_name }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.total_income')" min-width="150">
                <div slot-scope="{ row }" class="text-success">
                  {{ formatNumber(row.total_income) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.total_expense')" min-width="150">
                <div slot-scope="{ row }" class="text-danger">
                  {{ formatNumber(row.total_expense) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.net_amount')" min-width="150">
                <div slot-scope="{ row }" :class="row.net_amount >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatNumber(row.net_amount) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('account.count')" min-width="100">
                <div slot-scope="{ row }">
                  {{ row.record_count }}
                </div>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker, Tabs, TabPane } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { Badge, StatsCard } from "@/components";
import { mapActions, mapGetters } from "vuex";
import BarChart from "@/components/Charts/BarChart";
import * as chartConfigs from "@/components/Charts/config";

export default {
  components: {
    RouteBreadCrumb,
    Badge,
    StatsCard,
    BarChart,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
  },
  computed: {
    ...mapGetters({
      reportSummary: "Account/getReportSummary",
      reportByUser: "Account/getReportByUser",
      reportByItem: "Account/getReportByItem",
      reportByDate: "Account/getReportByDate",
    }),
    // Chart data computed from reportByDate
    dateChartData() {
      if (!this.reportByDate || this.reportByDate.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      // Sort by date
      const sortedData = [...this.reportByDate].sort((a, b) =>
        new Date(a.date) - new Date(b.date)
      );

      // Get all dates as labels
      const labels = sortedData.map(item => item.date);

      // Prepare income and expense data
      const incomeData = sortedData.map(item => parseFloat(item.total_income) || 0);
      const expenseData = sortedData.map(item => parseFloat(item.total_expense) || 0);

      return {
        labels: labels,
        datasets: [
          {
            label: this.$t('account.income'),
            data: incomeData,
            backgroundColor: '#2dce89',
            borderColor: '#2dce89',
            borderWidth: 1,
          },
          {
            label: this.$t('account.expense'),
            data: expenseData,
            backgroundColor: '#f5365c',
            borderColor: '#f5365c',
            borderWidth: 1,
          }
        ]
      };
    },
  },
  data() {
    return {
      loading: false,
      activeTab: "byItem",
      filterDateRange: [],
      filterType: 0,
      chartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
        },
        responsive: true,
        tooltips: {
          backgroundColor: '#f5f5f5',
          titleFontColor: '#333',
          bodyFontColor: '#666',
          bodySpacing: 4,
          xPadding: 12,
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || '';
              if (label) {
                label += ': ';
              }
              label += parseFloat(tooltipItem.yLabel).toLocaleString('th-TH', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              return label;
            }
          }
        },
        scales: {
          yAxes: [{
            gridLines: {
              drawBorder: false,
              color: 'rgba(29,140,248,0.1)',
              zeroLineColor: 'transparent'
            },
            ticks: {
              padding: 20,
              fontColor: '#9e9e9e',
              callback: function(value) {
                return value.toLocaleString('th-TH');
              }
            }
          }],
          xAxes: [{
            gridLines: {
              drawBorder: false,
              color: 'rgba(29,140,248,0.1)',
              zeroLineColor: 'transparent'
            },
            ticks: {
              padding: 20,
              fontColor: '#9e9e9e'
            }
          }]
        }
      }
    };
  },
  mounted() {
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.filterDateRange = [
      firstDay.toISOString().split('T')[0],
      lastDay.toISOString().split('T')[0],
    ];
    this.loadAllReports();
  },
  methods: {
    ...mapActions({
      fetchReportSummary: "Account/fetchReportSummary",
      fetchReportByUser: "Account/fetchReportByUser",
      fetchReportByItem: "Account/fetchReportByItem",
      fetchReportByDate: "Account/fetchReportByDate",
    }),
    getFilterParams() {
      const params = {};
      if (this.filterDateRange && this.filterDateRange.length === 2) {
        params.time_from = this.filterDateRange[0];
        params.time_to = this.filterDateRange[1];
      }
      if (this.filterType > 0) params.type = this.filterType;
      return params;
    },
    async loadAllReports() {
      this.loading = true;
      try {
        const params = this.getFilterParams();
        await Promise.all([
          this.fetchReportSummary(params),
          this.fetchReportByItem(params),
          this.fetchReportByUser(params),
          this.fetchReportByDate(params),
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleTabClick() {
      // Optional: reload specific report when tab changes
    },
    formatNumber(num) {
      if (num === null || num === undefined) return "0.00";
      return parseFloat(num).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
</style>

<style scoped>
/* Force left alignment for all table headers */
::v-deep .el-table th .cell {
  text-align: left !important;
}
</style>
